/**
 * Keyboard play: a tile cursor moved with the arrows, with keys for the actions. The letters are
 * read by their place on the keyboard, not by what they type (decision 0026): WASD is ZQSD on a
 * French keyboard, as French players expect, and the keys keep working while a Japanese, Korean
 * or Chinese input method turns letters into other characters. The strings name each language's
 * letters.
 */

export interface KeyHandlers {
  move(dx: number, dy: number): void;
  reveal(): void;
  banner(): void;
  home(): void;
  zoom(step: number): void;
  /** Turns the world by eighths of a turn, where the view can turn. */
  turn(step: number): void;
  /** Tilts the camera toward the ground (positive) or away from it. */
  tilt(step: number): void;
  escape(): void;
}

/** The letter keys by their place: the place of W on an English keyboard, whatever it types. */
function byPlace(code: string, h: KeyHandlers): boolean {
  switch (code) {
    case "KeyA":
      h.move(-1, 0);
      return true;
    case "KeyD":
      h.move(1, 0);
      return true;
    case "KeyW":
      h.move(0, -1);
      return true;
    case "KeyS":
      h.move(0, 1);
      return true;
    case "KeyF":
      h.banner();
      return true;
    case "KeyH":
      h.home();
      return true;
    case "KeyQ":
      h.turn(-1);
      return true;
    case "KeyE":
      h.turn(1);
      return true;
    case "KeyT":
      h.tilt(1);
      return true;
    case "KeyG":
      h.tilt(-1);
      return true;
    default:
      return false;
  }
}

/** The keys named by what they are, wherever a layout puts them: arrows, Enter, plus and minus. */
function byKey(key: string, h: KeyHandlers): boolean {
  switch (key) {
    case "ArrowLeft":
      h.move(-1, 0);
      return true;
    case "ArrowRight":
      h.move(1, 0);
      return true;
    case "ArrowUp":
      h.move(0, -1);
      return true;
    case "ArrowDown":
      h.move(0, 1);
      return true;
    case "Enter":
    case " ":
      h.reveal();
      return true;
    case "+":
    case "=":
      h.zoom(1);
      return true;
    case "-":
      h.zoom(-1);
      return true;
    case "Escape":
      h.escape();
      return true;
    default:
      return false;
  }
}

/** Acts on a key, by its place where it is a letter, else by what it is; false when not ours. */
export function handleKey(code: string, key: string, h: KeyHandlers): boolean {
  return byPlace(code, h) || byKey(key, h);
}

export function attachKeyboard(h: KeyHandlers): () => void {
  const onKey = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT")
    )
      return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    // A focused button takes Enter and Space for itself.
    if (target?.tagName === "BUTTON" && (e.key === "Enter" || e.key === " ")) return;
    if (handleKey(e.code, e.key, h)) e.preventDefault();
  };
  window.addEventListener("keydown", onKey);
  return () => window.removeEventListener("keydown", onKey);
}
